/* =========================================
   LAZY SWIPER MANAGER
========================================= */
(() => {
  'use strict';

  const SWIPER_SELECTOR = '.swiper-new';
  const instances = new WeakMap();
  const autoplayVisibility = new WeakMap();

  let intersectionObserver = null;
  let autoplayIntersectionObserver = null;

  /* -----------------------------------------
     HELPERS
  ----------------------------------------- */
  const parseNumber = (value, fallback) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getSwiperContainers = (root = document) => {
    const containers = [];

    if (root instanceof Element && root.matches(SWIPER_SELECTOR)) {
      containers.push(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      containers.push(...root.querySelectorAll(SWIPER_SELECTOR));
    }

    return containers;
  };

  const supportsDesktopMousewheel = () => {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  };

  const canAutoplay = (container) => {
    return container.dataset.autoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  const updateAutoplayVisibility = (container, isVisible) => {
    autoplayVisibility.set(container, isVisible);

    const swiper = instances.get(container) || container.swiper;
    if (!swiper || swiper.destroyed || !swiper.autoplay) return;

    const shouldRun = isVisible && document.visibilityState !== 'hidden';

    if (shouldRun && !swiper.autoplay.running) {
      swiper.autoplay.start();
    } else if (!shouldRun && swiper.autoplay.running) {
      swiper.autoplay.stop();
    }
  };

  const getAutoplayIntersectionObserver = () => {
    if (autoplayIntersectionObserver) return autoplayIntersectionObserver;
    if (!('IntersectionObserver' in window)) return null;

    autoplayIntersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          updateAutoplayVisibility(entry.target, entry.isIntersecting);
        });
      },
      {
        rootMargin: '0px',
        threshold: 0.01,
      },
    );

    return autoplayIntersectionObserver;
  };

  const observeAutoplayVisibility = (container) => {
    if (!canAutoplay(container)) return;

    const observer = getAutoplayIntersectionObserver();
    if (observer) {
      observer.observe(container);
    } else {
      autoplayVisibility.set(container, true);
    }
  };

  const getCurrentSlidesPerView = (container) => {
    const viewportWidth = window.innerWidth;

    if (viewportWidth >= 2560) return parseNumber(container.dataset.slidesXxl, 1);
    if (viewportWidth >= 1440) return parseNumber(container.dataset.slidesXl, 1);
    if (viewportWidth >= 1024) return parseNumber(container.dataset.slidesLg, 1);
    if (viewportWidth >= 768) return parseNumber(container.dataset.slidesMd, 1);
    if (viewportWidth >= 480) return parseNumber(container.dataset.slidesSm, 1);

    return parseNumber(container.dataset.slidesXs, 1);
  };

  const materializeDeferredSlides = (container, amount) => {
    const templates = Array.from(container.querySelectorAll('template[data-deferred-swiper-slide]')).slice(0, amount);

    templates.forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });

    if (templates.length && window.DCSWishlist?.updateButtons) {
      window.DCSWishlist.updateButtons();
    }

    return templates.length;
  };

  /* -----------------------------------------
     INITIALIZE ONE SWIPER
  ----------------------------------------- */
  const initSingleSwiper = (container) => {
    if (!container || !container.isConnected) return;

    if (instances.has(container)) return;

    if (container.dataset.swiperState === 'initializing' || container.dataset.swiperState === 'initialized') {
      return;
    }

    if (typeof window.Swiper !== 'function') {
      console.warn('Swiper library is unavailable:', container);
      return;
    }

    container.dataset.swiperState = 'initializing';

    const slidesXs = parseNumber(container.dataset.slidesXs, 1);

    const slidesSm = parseNumber(container.dataset.slidesSm, slidesXs);

    const slidesMd = parseNumber(container.dataset.slidesMd, slidesSm);

    const slidesLg = parseNumber(container.dataset.slidesLg, slidesMd);

    const slidesXl = parseNumber(container.dataset.slidesXl, slidesLg);

    const slidesXxl = parseNumber(container.dataset.slidesXxl, slidesXl);

    const spaceBetween = parseNumber(container.dataset.spaceBetween, 10);

    const loopRequested = container.dataset.loop === 'true';
    const autoplayRequested = canAutoplay(container);
    const autoplayDelay = Math.max(1000, parseNumber(container.dataset.autoplayDelay, 5000));
    const hasProgressiveSlides = container.dataset.progressiveSlides === 'true';

    /*
      Swiper must know every slide before creating loop duplicates.
      Keep progressive rendering for normal carousels, but materialize
      all deferred slides when a merchant explicitly enables looping.
    */
    if (loopRequested && hasProgressiveSlides) {
      materializeDeferredSlides(container, container.querySelectorAll('template[data-deferred-swiper-slide]').length);
    }

    const usesProgressiveSlides = hasProgressiveSlides && !loopRequested;

    if (usesProgressiveSlides) {
      const initialSlideTarget = Math.ceil(getCurrentSlidesPerView(container)) + 1;
      const renderedSlideCount = container.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
      materializeDeferredSlides(container, Math.max(0, initialSlideTarget - renderedSlideCount));
    }

    const slides = container.querySelectorAll('.swiper-wrapper > .swiper-slide');
    const wrapper = container.querySelector('.swiper-wrapper');
    const usesListSemantics = wrapper?.matches('ul, ol, [role="list"]');

    const slideCount = slides.length;

    const maximumSlidesPerView = Math.max(slidesXs, slidesSm, slidesMd, slidesLg, slidesXl, slidesXxl);
    const requiredSlidesForLoop = loopRequested
      ? Math.ceil(getCurrentSlidesPerView(container))
      : Math.ceil(maximumSlidesPerView);

    /*
      Looping creates duplicate slides and adds layout work.

      Enable it only when the carousel has more slides than
      the largest configured slides-per-view value.
    */
    const shouldLoop =
      !usesProgressiveSlides && container.dataset.loop !== 'false' && slideCount > requiredSlidesForLoop;

    const loadNextSlideBatch = (swiper) => {
      if (!usesProgressiveSlides) return;

      const remainingTemplates = container.querySelector('template[data-deferred-swiper-slide]');
      if (!remainingTemplates) return;

      const visibleSlides = Math.ceil(Number(swiper.params.slidesPerView) || getCurrentSlidesPerView(container));
      const isNearRenderedEnd = swiper.activeIndex + visibleSlides >= swiper.slides.length - 1;

      if (!isNearRenderedEnd) return;

      if (materializeDeferredSlides(container, Math.max(2, visibleSlides))) {
        swiper.update();
      }
    };

    try {
      const swiper = new window.Swiper(container, {
        direction: 'horizontal',

        slidesPerView: slidesXs,
        spaceBetween,

        watchOverflow: true,

        // Keep list wrappers paired with list items when Swiper adds slide roles.
        a11y: {
          slideRole: usesListSemantics ? 'listitem' : 'group',
        },

        /*
          Prevent Swiper from loading all images eagerly.
          Keep this only if your current Swiper version
          supports this lazy-loading configuration.
        */
        preloadImages: false,

        lazy: {
          enabled: true,
          loadPrevNext: true,
          loadOnTransitionStart: false,
        },

        freeMode: {
          enabled: true,
          momentum: true,
          momentumBounce: true,
          sticky: false,
        },

        /*
          Mousewheel is useful on desktop, but unnecessary
          on touch devices and mobile phones.
        */
        mousewheel: supportsDesktopMousewheel()
          ? {
              forceToAxis: true,
              sensitivity: 1,
              releaseOnEdges: true,
            }
          : false,

        loop: shouldLoop,

        rewind: autoplayRequested && !shouldLoop,

        autoplay: autoplayRequested
          ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
              waitForTransition: true,
            }
          : false,

        breakpoints: {
          0: {
            slidesPerView: slidesXs,
          },

          480: {
            slidesPerView: slidesSm,
          },

          768: {
            slidesPerView: slidesMd,
          },

          1024: {
            slidesPerView: slidesLg,
          },

          1440: {
            slidesPerView: slidesXl,
          },

          2560: {
            slidesPerView: slidesXxl,
          },
        },

        on: {
          init() {
            container.dataset.swiperState = 'initialized';

            if (autoplayRequested && autoplayVisibility.get(container) !== true) {
              this.autoplay?.stop();
            }
          },

          slideChange() {
            loadNextSlideBatch(this);
          },

          reachEnd() {
            loadNextSlideBatch(this);
          },

          destroy() {
            delete container.dataset.swiperState;
          },
        },
      });

      instances.set(container, swiper);
      container.dataset.swiperState = 'initialized';

      if (autoplayRequested) {
        updateAutoplayVisibility(container, autoplayVisibility.get(container) === true);
      }
    } catch (error) {
      delete container.dataset.swiperState;

      console.error('Swiper initialization failed:', container, error);
    }
  };

  /* -----------------------------------------
     SCHEDULE INITIALIZATION

     This avoids initializing several sliders
     in the same rendering frame.
  ----------------------------------------- */
  const scheduleSwiperInitialization = (container) => {
    if (!container || instances.has(container)) return;

    const run = () => {
      initSingleSwiper(container);
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, {
        timeout: 500,
      });
    } else {
      window.requestAnimationFrame(run);
    }
  };

  /* -----------------------------------------
     SHARED INTERSECTION OBSERVER
  ----------------------------------------- */
  const getIntersectionObserver = () => {
    if (intersectionObserver) {
      return intersectionObserver;
    }

    if (!('IntersectionObserver' in window)) {
      return null;
    }

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          intersectionObserver.unobserve(entry.target);
          scheduleSwiperInitialization(entry.target);
        });
      },
      {
        /*
          Begin initialization before the slider becomes
          visible, without initializing the whole page.
        */
        rootMargin: '400px 0px',
        threshold: 0,
      },
    );

    return intersectionObserver;
  };

  /* -----------------------------------------
     DISCOVER NEW SWIPERS
  ----------------------------------------- */
  const observeSwipers = (root = document) => {
    const containers = getSwiperContainers(root);

    if (!containers.length) return;

    const observer = getIntersectionObserver();

    containers.forEach((container) => {
      if (instances.has(container)) return;

      if (container.dataset.swiperState === 'initializing' || container.dataset.swiperState === 'initialized') {
        return;
      }

      observeAutoplayVisibility(container);

      if (observer) {
        observer.observe(container);
      } else {
        scheduleSwiperInitialization(container);
      }
    });
  };

  window.DCSObserveSwipers = observeSwipers;

  /* -----------------------------------------
     DESTROY SWIPERS IN REMOVED SECTION
  ----------------------------------------- */
  const destroySwipers = (root) => {
    const containers = getSwiperContainers(root);

    containers.forEach((container) => {
      intersectionObserver?.unobserve(container);
      autoplayIntersectionObserver?.unobserve(container);

      const swiper = instances.get(container) || container.swiper;

      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }

      instances.delete(container);
      autoplayVisibility.delete(container);
      delete container.dataset.swiperState;
    });
  };

  /* -----------------------------------------
     INITIAL PAGE LOAD
  ----------------------------------------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observeSwipers(document), { once: true });
  } else {
    observeSwipers(document);
  }

  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll(`${SWIPER_SELECTOR}[data-autoplay='true']`).forEach((container) => {
      updateAutoplayVisibility(container, autoplayVisibility.get(container) === true);
    });
  });

  /* -----------------------------------------
     SHOPIFY THEME EDITOR
  ----------------------------------------- */
  document.addEventListener('shopify:section:load', (event) => {
    observeSwipers(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySwipers(event.target);
  });
})();
